import {
  PlayOutlineIc,
  ChevronBackOutlineIc,
  ChevronForwardOutlineIc,
  RepeatOutlineIc,
} from '@dsch/dd-icons';

import { MediaCount } from 'components/Toolkit/Tags/MediaCount';

import {
  Gallery,
  GALLERY_INITIAL_MEDIA_INDEX_LAST_FLAG,
} from 'components/Toolkit/Gallery/Gallery';
import { useGalleryOnPage } from 'components/Toolkit/Gallery/GalleryOnPage.hook';
import type { GalleryOnPageProps } from 'components/Toolkit/Gallery/GalleryOnPage.typed';
import * as Styled from 'components/Toolkit/Gallery/GalleryOnPage.styled';

function GalleryOnPage(props: GalleryOnPageProps) {
  const {
    className,
    photo,
    videoConfig,
    link360,
    link360Interior,
    mediaCount,
    title,
    youTubeVideoId,
    youTubeReviewVideoId,
    isYoutubeReviewOpen,
    setIsYoutubeReviewOpen,
    isDealer,
    trackingId,
    reportOnGalleryUsage,
    isGalleryOpen,
    setIsGalleryOpen,
    cdnUrl,
    imageCounterForDFP,
    setImageCounterForDFP,
    isPreview,
    refreshTopAndSideDFPUnit,
    ...rest
  } = props;

  const displayImage =
    videoConfig?.customProperties?.thumbnail_url_0 || (photo && photo.medium);

  const { openGallery, closeGallery } = useGalleryOnPage({
    reportOnGalleryUsage,
    setIsGalleryOpen,
    displayImage,
  });

  return (
    <>
      <Styled.Wrapper
        className={className}
        onClick={openGallery}
        data-tracking={trackingId}
        isDealer={isDealer}
        isPreview={isPreview}
      >
        {youTubeVideoId && (
          <Styled.Ssvg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 176 124"
          >
            <path
              d="M172.32 19.36A22.12 22.12 0 00156.76 3.7C143 0 88 0 88 0S33 0 19.24 3.7A22.12 22.12 0 003.68 19.36C0 33.18 0 62 0 62s0 28.82 3.68 42.64a22.12 22.12 0 0015.56 15.66C33 124 88 124 88 124s55 0 68.76-3.7a22.12 22.12 0 0015.56-15.66C176 90.82 176 62 176 62s0-28.82-3.68-42.64z"
              fill="#ee3124"
            />
            <path fill="#fff" d="M70 88.17V35.83L116 62 70 88.17z" />
          </Styled.Ssvg>
        )}
        {!isPreview && (
          <Styled.SPrevious>
            <ChevronBackOutlineIc width="36" height="36" />
          </Styled.SPrevious>
        )}
        <Styled.Img src={displayImage} alt={title} />
        {!isPreview && (
          <Styled.SNext>
            <ChevronForwardOutlineIc width="36" height="36" />
          </Styled.SNext>
        )}
        <Styled.Overview>
          {!isPreview && <MediaCount mediaCount={mediaCount} />}
          {youTubeVideoId && (
            <Styled.SMediaHighlight>
              <PlayOutlineIc width="16" height="16" />
            </Styled.SMediaHighlight>
          )}
          {youTubeReviewVideoId && setIsYoutubeReviewOpen && (
            <Styled.WatchReview
              onClick={() => {
                setIsYoutubeReviewOpen(true);
              }}
              data-tracking="watch-review"
            >
              <PlayOutlineIc width="16" height="16" />
              <span>Watch Review</span>
            </Styled.WatchReview>
          )}
          {(link360 || link360Interior) && (
            <Styled.SMediaHighlight>
              <RepeatOutlineIc height="16" width="16" />
            </Styled.SMediaHighlight>
          )}
        </Styled.Overview>
      </Styled.Wrapper>
      {isGalleryOpen && (
        <Gallery
          isOpen={isGalleryOpen}
          askToClose={closeGallery}
          link360={link360}
          link360Interior={link360Interior}
          videoConfig={videoConfig}
          youTubeVideoId={youTubeVideoId}
          youTubeReviewVideoId={youTubeReviewVideoId}
          initialMediaIndex={
            isYoutubeReviewOpen ? GALLERY_INITIAL_MEDIA_INDEX_LAST_FLAG : 0
          }
          cdnUrl={cdnUrl}
          imageCounterForDFP={imageCounterForDFP}
          setImageCounterForDFP={setImageCounterForDFP}
          refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
          title={title}
          {...rest}
        />
      )}
    </>
  );
}

export { GalleryOnPage };
